import { NetworkStatus } from "@apollo/client";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import usePolledQuery from "apollo/usePolledQuery";
import NotFoundPage from "components/error/NotFoundPage";
import ListEntitiesNew from "components/ListEntitiesNew";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { URL_SEARCH_KEY } from "constants/url_query_keys";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedFlags from "hooks/useTypedFlags";
import { fuzzySearch } from "utils/fuzzySearch";

import { PollingIntervalGroups } from "../../../apollo/constants";
import SharedModulesEmptyState from "./components/EmptyState";
import ModuleVirtualizedListItem from "./components/ListItem/Virtualized";
import SharedModulesPageLayout from "./components/PageLayout";
import SharedModulesTable from "./components/Table";
import { GET_SHARED_MODULES } from "./gql";

const SharedModules = () => {
  const { sharingModulesBetweenSpaces } = useTypedFlags();

  const { error, loading, data, networkStatus } = usePolledQuery(GET_SHARED_MODULES, {
    pollingGroup: PollingIntervalGroups.Lists,
    // avoid request executing twice while fetchMore
    nextFetchPolicy: "cache-first",
    // APOLLO CLIENT UPDATE
  });

  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get(URL_SEARCH_KEY);

  const modules = useMemo(() => {
    const filteredModules = data?.sharedModules || [];

    if (searchQuery) {
      return fuzzySearch(filteredModules, searchQuery.trim(), {
        keys: ["description", "id", "labels", "name"],
        scoreThreshold: -1000,
      });
    }

    return filteredModules;
  }, [data, searchQuery]);

  const ErrorContent = useErrorHandle(error);
  const initialLoading = loading && networkStatus === NetworkStatus.loading;

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading && !data?.sharedModules) {
    return (
      <SharedModulesPageLayout>
        <PageLayoutSkeleton />
      </SharedModulesPageLayout>
    );
  }

  if (networkStatus !== NetworkStatus.refetch && !loading && !data?.sharedModules) {
    return <NotFoundPage />;
  }

  return (
    <SharedModulesPageLayout>
      {data && !modules.length && <SharedModulesEmptyState didPerformSearch={!!searchQuery} />}

      {!sharingModulesBetweenSpaces && data && modules.length > 0 && (
        <ListEntitiesNew
          itemCount={modules.length}
          itemProps={{
            items: modules,
          }}
          virtualizedItem={ModuleVirtualizedListItem}
          itemKey={(index) => modules[index].id}
        />
      )}
      {sharingModulesBetweenSpaces && data && modules.length > 0 && (
        <SharedModulesTable sharedModules={modules} loadingContent={initialLoading} />
      )}
    </SharedModulesPageLayout>
  );
};

export default SharedModules;
